<template>
    <div v-loading="loading" class="user-portfolio-comments">
        <div class="user-portfolio-comments__head">Скрытые комментарии</div>
        <div
            v-if="commentsSorted.length"
            class="user-portfolio-comments__content"
        >
            <div
                v-for="(group, groupIndex) in commentsSorted"
                :key="`comment_group_${groupIndex}_${group.date}`"
                class="user-portfolio-comments-group"
            >
                <div class="user-portfolio-comments-group__date">
                    {{ group.date }}
                </div>
                <div class="user-portfolio-comments-group__list">
                    <PortfolioCommentsItem
                        v-for="(comment) in group.items"
                        :key="`comment_${comment.id}`"
                        :comment="comment"
                        :highlightKeywords="highlightKeywords"
                        @change="reloadList"
                    />
                </div>
            </div>
        </div>
        <div class="user-portfolio-comments__comment">
            <div class="user-portfolio-comments__comment-avatar">
                <img class="user-portfolio-comments__comment-avatar-img" v-if="currentUserImage" :src="currentUserImage" alt="" />
                <img class="user-portfolio-comments__comment-avatar-img" v-else src="~@/assets/images/account/placeHolder-m.png" alt="" />
            </div>
            <div class="user-portfolio-comments__comment-fields">
                <textarea :disabled="loading" ref="commentInput" v-model="newComment"></textarea>
                <button :disabled="loading || newComment.length === 0" @click="createNewComment">Опубликовать</button>
            </div>
        </div>
    </div>
</template>

<script>
import PortfolioCommentsItem from './PortfolioCommentsItem.vue';
import {mapActions, mapGetters} from "vuex";
import moment from "moment";
import {getHighlightGroupsKeywordsSet} from "@/tools/graphQueryHelpers";

export default {
    components: {
        PortfolioCommentsItem,
    },

    refs: ['commentInput'],

    props: {
        highlights: {
            type: Object,
            required: false,
            default: () => null,
        },
    },

    data: () => ({
        loading: false,
        newComment: '',
    }),

    async created() {
        await this.reloadList();
    },

    watch: {
        async 'getUser.userId'() {
            await this.reloadList();
        },

        newComment() {
            const textarea = this.$refs.commentInput;
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        },
    },

    computed: {
        ...mapGetters([
            'userProfileInfo',
        ]),
        ...mapGetters('newUsers', [
            'getUser',
            'getUserComments',
        ]),

        highlightKeywords() {
            return Array.from(
                getHighlightGroupsKeywordsSet(this.highlights, [
                    'comments.comment',
                ])
            );
        },

        commentsSorted() {
            const groups = {};
            const currentDateTime = new Date();
            const currentDate = moment(currentDateTime).format('DD.MM.YYYY');

            this.getUserComments.forEach(comment => {
                const dateTime = moment(comment.time);
                let groupDate = dateTime.format('DD.MM.YYYY');
                const sort = parseInt(dateTime.format('YYYY.MM.DD'));

                if (groupDate === currentDate) {
                    groupDate = 'Сегодня';
                }

                let group = groups[groupDate];

                if (group === undefined) {
                    group = groups[groupDate] = { date: groupDate, sort, items: [] };
                }

                comment.timeString = dateTime.diff(currentDateTime) + 30e3 > 0
                    ? 'Только что'
                    : dateTime.format('HH:mm')
                ;

                group.items.push(comment);
            });

            return Object.keys(groups)
                .map(key => {
                    const group = groups[key];

                    group.items.sort((a, b) => {
                        return a.sort === b.sort ? 0 : a.sort > b.sort ? -1 : 1;
                    });

                    return group;
                })
            ;
        },

        currentUserImage() {
            const user = this.userProfileInfo;

            return user.avatar || user.avatarOfficial || null;
        },
    },

    methods: {
        ...mapActions('newUsers', [
            'loadUserComments',
            'createUserComment',
        ]),

        async reloadList() {
            this.loading = true;

            await this.loadUserComments(this.getUser.userId);

            this.loading = false;
        },

        getName(item) {
            return [
                item.author.surname,
                item.author.name,
                item.author.patronimic,
            ].filter(v => v).join(' ');
        },

        async createNewComment() {
            this.loading = true;

            await this.createUserComment({
                userId: this.getUser.userId,
                text: this.newComment,
            });

            this.newComment = '';

            await this.reloadList();

            this.loading = false;
        },
    },
};

</script>

<style scoped lang="scss">
@import "~@/styles/rem";
@import '~@/styles/variables';

.user-portfolio-comments {
    display: flex;
    flex-direction: column;
    gap: mrem(40);

    @include on-desktop {
        gap: rem(40);
    }

    &__head {
        font-weight: 700;
        font-size: mrem(18);
        line-height: mrem(24);

        @include on-desktop {
            font-size: rem(18);
            line-height: rem(24);
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: mrem(40);

        @include on-desktop {
            gap: rem(40);
        }

        .user-portfolio-comments-group {
            display: flex;
            flex-direction: column;
            gap: mrem(16);

            @include on-desktop {
                gap: rem(16);
            }

            &__date {
                font-weight: 500;
                font-size: mrem(14);
                line-height: mrem(20);

                @include on-desktop {
                    font-size: rem(14);
                    line-height: rem(20);
                }
            }

            &__list {
                display: flex;
                flex-direction: column;
                gap: mrem(40);

                @include on-desktop {
                    gap: rem(40);
                }

                .user-portfolio-comments-item {
                    display: grid;
                    grid-template-columns: mrem(40) auto;
                    gap: mrem(16);

                    @include on-desktop {
                        grid-template-columns: rem(40) auto;
                        gap: rem(16);
                    }

                    &__image {
                        img {
                            width: 100%;
                            object-fit: contain;
                            border-radius: mrem(40);

                            @include on-desktop {
                                border-radius: rem(40);
                            }
                        }
                    }

                    &__content {
                        display: flex;
                        flex-direction: column;
                        gap: mrem(3);

                        @include on-desktop {
                            gap: rem(3);
                        }
                    }
                    &__title {
                        display: grid;
                        grid-template-columns: mrem(210) auto;
                        gap: mrem(40);

                        @include on-desktop {
                            grid-template-columns: rem(210) auto;
                            gap: rem(40);
                        }
                    }
                    &__name {
                        font-weight: 600;
                    }
                    &__date {
                        color: $color-main-grey;
                        font-weight: 400;
                        font-size: mrem(14);
                        line-height: mrem(20);

                        @include on-desktop {
                            font-size: rem(14);
                            line-height: rem(20);
                        }
                    }
                    &__text {
                        font-weight: 400;
                    }
                }
            }
        }
    }

    &__comment {
        display: grid;
        grid-template-columns: mrem(40) auto;
        gap: mrem(16);

        @include on-desktop {
            grid-template-columns: rem(40) auto;
            gap: rem(16);
        }

        &-avatar {
              display: flex;
              overflow: hidden;
              align-items: center;
              position: relative;
              border-radius: 50%;
              max-width: mrem(40);
              max-height: mrem(40);

              @include on-desktop {
                  max-width: rem(40);
                  max-height: rem(40);
              }
            img {
              position: absolute;
              object-fit: cover;
            }
        }
        &-fields {
            display: flex;
            flex-direction: column;
            gap: mrem(16);

            @include on-desktop {
                gap: rem(16);
            }

            textarea {
                width: 100%;
                font: inherit;
                padding: 1em;
                margin: 0;
                resize: none;
                background: $color-main-notice-lighter;
                appearance: none;
                border: none;
                min-height: mrem(80);
                border-radius: mrem(16);

                @include on-desktop {
                    min-height: rem(80);
                    border-radius: rem(16);
                }
            }
            button {
                flex: 0 1 auto;
                display: flex;
                padding: 11px 32px;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                border: none;
                background: $color-main-blue;
                color: #fff;
                text-align: center;
                font-weight: 700;
                cursor: pointer;
                width: mrem(174);
                border-radius: mrem(10);
                font-size: mrem(14);
                line-height: mrem(26);

                @include on-desktop {
                    width: rem(174);
                    border-radius: rem(10);
                    font-size: rem(14);
                    line-height: rem(26);
                }

                &:disabled {
                    opacity: 0.35;
                    cursor: not-allowed;
                }
            }
        }
    }
}

</style>
